const HASH_SIZE = 8;
const SAMPLE_SIZE = 32;
const MAX_DEDUP_IMAGES = 100;

export async function computeHash(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_SIZE;
  canvas.height = SAMPLE_SIZE;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bitmap, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
  bitmap.close();

  const { data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

  const grey: number[] = [];
  for (let i = 0; i < data.length; i += 4) {
    grey.push(data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);
  }

  const dctBlock: number[] = [];
  for (let u = 0; u < HASH_SIZE; u++) {
    for (let v = 0; v < HASH_SIZE; v++) {
      let sum = 0;
      for (let x = 0; x < SAMPLE_SIZE; x++) {
        for (let y = 0; y < SAMPLE_SIZE; y++) {
          sum +=
            grey[x * SAMPLE_SIZE + y] *
            Math.cos(((2 * x + 1) * u * Math.PI) / (2 * SAMPLE_SIZE)) *
            Math.cos(((2 * y + 1) * v * Math.PI) / (2 * SAMPLE_SIZE));
        }
      }
      dctBlock.push(sum);
    }
  }

  const withoutDC = dctBlock.slice(1);
  const sorted = [...withoutDC].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];

  return dctBlock.map((v) => (v > median ? '1' : '0')).join('');
}

export function hammingDistance(a: string, b: string): number {
  let dist = 0;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) dist++;
  }
  return dist;
}

interface HashedImage {
  file: File;
  hash: string;
  width: number;
  height: number;
}

export function deduplicateByHash(
  images: HashedImage[],
  threshold = 5,
): { kept: HashedImage[]; removedCount: number } {
  if (images.length === 0) return { kept: [], removedCount: 0 };
  if (images.length > MAX_DEDUP_IMAGES) return { kept: images, removedCount: 0 };

  const removed = new Set<number>();

  for (let i = 0; i < images.length; i++) {
    if (removed.has(i)) continue;
    for (let j = i + 1; j < images.length; j++) {
      if (removed.has(j)) continue;
      if (hammingDistance(images[i].hash, images[j].hash) < threshold) {
        const pixelsI = images[i].width * images[i].height;
        const pixelsJ = images[j].width * images[j].height;
        removed.add(pixelsI >= pixelsJ ? j : i);
        if (removed.has(i)) break;
      }
    }
  }

  const kept = images.filter((_, i) => !removed.has(i));
  return { kept, removedCount: removed.size };
}
